// SpotBidding 通常入札タブ用の業務 validation / 集計ロジック (= frontend pure 関数)。
//
// 入札制限違反 (detectLimitViolations) は settings「入札制限」(bid-limits) の実設定値
// (= BFF GetBidLimitMatrix) を照合する (= `~/models/bidLimits`)。それ以外の集計 (KPI /
// ポジション / 約定集計) は TODO(business-logic) の通り本来 backend 由来だが Phase 1 は
// frontend pure 関数で再現し、proto / backend 拡張後に BFF 計算済み値へ置換予定。
import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { BiddingSpotBidStatus } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import i18next from 'i18next'

import type { CoverageTone } from '~/components/bidding/common/coverageColors'
import {
  type CoverageState,
  resolveCoverageState,
  resolveRemainMw,
} from '~/components/bidding/common/coverageColors'
import {
  addToKomaCoverLines,
  coverBidMw,
  createKomaCoverLines,
  readKomaLine,
} from '~/components/bidding/common/positionLineTotals'
import type { PositionChartSlot } from '~/components/bidding/common/types'
import { convertAreaLabel } from '~/models/area'
import {
  type AreaSideLimit,
  type BidLimits,
  type VolumeTotalViolation,
  checkBidLimit,
  resolveAreaSideLimit,
} from '~/models/bidLimits'
import { sumKomaMwToMwh } from '~/models/komaQuantity'
import {
  JEPX_PRICE_CONSTRAINT,
  JEPX_VOLUME_CONSTRAINT,
  violatesJepxStep,
} from '~/models/numberInputConstraint'
import { unknownIssuerBidMw } from '~/models/unknownIssuerBid'

import { UNKNOWN_ISSUER_COUNTED_CATEGORIES } from './bidStatusCategory'
import { type SpotBidRow, isMarketOrderRow } from './spotBidRow'
import {
  type SpotBidStatusCategory,
  type SpotBidStatusCategoryInput,
  coverLineOfSpotCategory,
  deriveSpotBidStatusCategory,
} from './spotBidStatusCategory'

// ----- 入札制限違反検出 (= settings「入札制限」の実設定値ベース) -----

export type LimitViolation = {
  bidId: string
  kind: 'price-min' | 'price-max' | 'volume-max'
  /** 対象エリア (= AC-BSP-008 が承認申請確認モーダルに要求する違反明細の項目)。 */
  areaCode: AreaCode
  /** 売買方向 (= 制限は エリア × 売買 で引くため明細にも出す)。 */
  side: Side
  /** コマ番号 (1-origin)。違反行を一覧で特定するために出す。 */
  timeCode: number
  /** 実際の入札値 (価格違反なら 円/kWh、数量違反なら MW)。 */
  actual: number
  /** 当該 (エリア × 売買) の解決済み制限。明細に許容レンジを出すため丸ごと持つ。 */
  limit: AreaSideLimit
}

/**
 * 札行ごとに settings「入札制限」の実設定値 (エリア × 売買の価格上下限・上限数量) との
 * 違反を検出する。制限が未設定の (エリア × 売買) は「制限未設定 = 違反なし」とする
 * (= mock フォールバックは持たない)。
 *
 * 成行 (AC-SPOT-006) の札は価格を持たない (`priceYenPerKwh` の "0" は指値 0 円では
 * ない) ので価格軸を外し、数量軸だけを判定する。backend の `Matrix.CheckBid` が
 * 成行に `option.None` の価格を渡して MIN / MAX 軸を外すのと同じ規則で、ここを
 * 揃えないと同じ札をサーバは許可・クライアントはブロックする状態になる。
 *
 * 取下が成立した (約定軸 CANCELLED、AC-BSP-081) 札は板から下りて枠が解放されるため
 * 判定対象から外す (spec Discussion「取消・失効の札は上限計算…から除外し、取消枠は
 * 解放する」)。取下処理中 (WITHDRAWING) はまだ成立していないため外さない。
 *
 * @param bids - 検証対象の札一覧
 * @param limits - BFF GetBidLimitMatrix から組んだ (エリア × 売買) 別制限
 */
export const detectLimitViolations = (
  bids: SpotBidRow[],
  limits: BidLimits,
): LimitViolation[] => {
  const out: LimitViolation[] = []
  for (const b of bids) {
    if (b.bidStatus === BiddingSpotBidStatus.CANCELLED) continue
    const limit = resolveAreaSideLimit(limits, b.areaCode, b.direction)
    if (limit === undefined) continue
    const price = isMarketOrderRow(b) ? undefined : Number(b.priceYenPerKwh)
    // 制限 QTY も札の数量も MW (issue #4285)。 単位換算は挟まない。
    const volumeMw = Number(b.volumeMwh)
    for (const kind of checkBidLimit(price, volumeMw, limit)) {
      out.push({
        bidId: b.id,
        kind,
        areaCode: b.areaCode,
        side: b.direction,
        timeCode: b.timeCode,
        // price 側の kind は price が undefined (= 成行) では出ないので、
        // ここは必ず判定に使った値と一致する。
        actual: kind === 'volume-max' ? volumeMw : Number(b.priceYenPerKwh),
        limit,
      })
    }
  }
  return out
}

export const sideLabelOf = (side: Side): string => {
  const t = i18next.t.bind(i18next)
  return side === Side.BUY
    ? t('common:tradeSide.buy')
    : t('common:tradeSide.sell')
}

/** 明細行の共通見出し (design `bsc-violation-list` / `bsc-stairs-list` 準拠)。 */
export const detailHead = (
  timeCode: number,
  areaCode: AreaCode,
  side: Side,
): string =>
  i18next.t('page:biddingSpot.violation.detailHead', {
    timeCode,
    area: convertAreaLabel(areaCode),
    side: sideLabelOf(side),
  })

/** 制限未設定の軸に出す「なし」相当のラベル。 */
const noLimitLabel = (): string =>
  i18next.t('page:biddingSpot.violation.noLimit')

const yenOrNone = (v: number | undefined): string =>
  v !== undefined ? `¥${v.toFixed(2)}` : noLimitLabel()

/**
 * 価格違反 1 行の整形。 どこの価格かを示す `head` は呼び出し元が決める (通常入札は札の
 * コマ、 ブロック入札は leg のコマ範囲)。
 */
export const formatLimitPriceDetail = (
  head: string,
  actual: number,
  limit: AreaSideLimit,
): string =>
  i18next.t('page:biddingSpot.violation.limitPriceDetail', {
    head,
    actual: actual.toFixed(2),
    min: yenOrNone(limit.priceMin),
    max: yenOrNone(limit.priceMax),
  })

/**
 * 違反 1 件を承認申請確認モーダル用の 1 行明細に整形する (AC-BSP-008 が要求する
 * 「対象エリア、設定上限/下限、実際の入札値」を 1 行に載せる)。
 *
 * 書式は design `spot.html` の `bsc-violation-list` 準拠:
 *   価格: `コマ 25 ・ 東京 ・ 売: 価格 ¥12.50（許容 ¥5.00〜¥100.00）`
 *   数量: `コマ 25 ・ 東京 ・ 売: 数量 60 MW（上限 50 MW）`
 *
 * 承認確認 (`scene` = approve) の数量は、 AC-BSP-063 の承認操作時の規定文言
 * ({制限項目} = 上限数量(1札)、 {該当値} = 対象の札) に同じ見出しを前置する。
 */
export const formatLimitViolationDetail = (
  v: LimitViolation,
  scene: VolumeTotalViolationScene = 'request',
): string => {
  const t = i18next.t.bind(i18next)
  const head = detailHead(v.timeCode, v.areaCode, v.side)
  if (v.kind === 'volume-max') {
    const qtyMax =
      v.limit.qtyMax !== undefined ? `${v.limit.qtyMax} MW` : noLimitLabel()
    return t(
      scene === 'approve'
        ? 'page:biddingSpot.violation.limitVolumeDetailApprove'
        : 'page:biddingSpot.violation.limitVolumeDetail',
      { head, actual: v.actual, max: qtyMax },
    )
  }
  return formatLimitPriceDetail(head, v.actual, v.limit)
}

// ----- 上限数量(コマ計) 超過検出 (AC-BSP-080 / AC-BSP-097、 bid-limits.md §1-2) -----
//
// 合算の判定本体は時間前入札画面 (AC-BIN-015 ※) も使うので `~/models/bidLimits` に置く。
// 母集合 (どの札を合算するか) は判定対象 (下書きの申請対象 / 承認対象の申請セット) ごとに
// 異なるため、 呼び出し元が母集合を組んで渡す (= `detectStairsExcesses` と同じ設計。
// `buildSpotViolationSections` / `bulkCreate.ts buildBulkPreviewRows` 参照)。
// ブロック入札の当該コマ数量は呼び出し元が母集合に足す (`selectRevalidationBlockVolumes`)。
export { detectVolumeTotalViolations } from '~/models/bidLimits'
export type {
  VolumeCountableBid,
  VolumeTotalViolation,
} from '~/models/bidLimits'

/** 上限数量(コマ計) 超過の明細を出す場面 (AC-BSP-063 は場面ごとに文言が違う)。 */
export type VolumeTotalViolationScene =
  | 'request'
  | 'submit'
  | 'mixed'
  | 'approve'

const VOLUME_TOTAL_DETAIL_KEYS = {
  request: 'page:biddingSpot.violation.volumeTotalDetailRequest',
  submit: 'page:biddingSpot.violation.volumeTotalDetailSubmit',
  mixed: 'page:biddingSpot.violation.volumeTotalDetailMixed',
  approve: 'page:biddingSpot.violation.volumeTotalDetailApprove',
} as const satisfies Record<VolumeTotalViolationScene, string>

/**
 * コマ計上限超過 1 件を承認申請確認 / 直接提出確認 / 承認確認モーダルの 1 行明細に整形する
 * (AC-BSP-080 / AC-BSP-097)。 文言は AC-BSP-063 の規定文言どおりで、 エリア・コマ・売買は
 * 入れない (#8245)。
 */
export const formatVolumeTotalViolationDetail = (
  v: VolumeTotalViolation,
  scene: VolumeTotalViolationScene,
): string =>
  i18next.t(VOLUME_TOTAL_DETAIL_KEYS[scene], {
    actual: v.actual,
    limit: v.limit,
  })

// ----- JEPX 制度上限 (AC-BSP-050 / AC-BSP-052) -----
//
// テナント設定の入札制限 (detectLimitViolations) とは **独立した** 検証 (AC-BSP-052)。
// あちらは組織ごとに設定される任意の上下限で、未設定なら検証しない。こちらは JEPX の
// 制度仕様なので全組織・全エリア・全売買方向に無条件で効く。違反も別種別として扱い、
// 別セクション・別文言で表示する。
//
// 値の出典は spec spot-bidding.md §7「ビジネスルール」表 + AC-BSP-050。
// 価格・数量とも `~/models/numberInputConstraint` の JEPX 定数が SoT (= 入力欄の
// min/max/step と提出可否判定の二重定義 drift を防ぐ)。

/** JEPX 制度による価格の下限 (円/kWh)。 */
export const JEPX_PRICE_MIN_YEN_PER_KWH = JEPX_PRICE_CONSTRAINT.min

/** JEPX 制度による価格の上限 (円/kWh)。 */
export const JEPX_PRICE_MAX_YEN_PER_KWH = JEPX_PRICE_CONSTRAINT.max

/**
 * JEPX 制度による数量の下限 (MW)。JEPX 最小入札単位 0.05 MWh/30分 に相当する
 * (= 1 コマ 0.05 MWh を平均電力にすると 0.1 MW)。
 */
export const JEPX_VOLUME_MIN_MW = JEPX_VOLUME_CONSTRAINT.min

/** JEPX 制度による数量の上限 (MW)。 */
export const JEPX_VOLUME_MAX_MW = JEPX_VOLUME_CONSTRAINT.max

/** JEPX 制度による数量の刻み (MW)。 spec AC-BSP-050 の「0.1 MW 刻み」。 */
export const JEPX_VOLUME_STEP_MW = JEPX_VOLUME_CONSTRAINT.step

export type JepxLimitViolationKind =
  | 'price-min'
  | 'price-max'
  | 'price-step'
  | 'volume-min'
  | 'volume-max'
  | 'volume-step'

export type JepxLimitViolation = {
  bidId: string
  kind: JepxLimitViolationKind
  areaCode: AreaCode
  side: Side
  timeCode: number
  /** 実際の入札値 (価格違反なら 円/kWh、数量違反なら MW)。 */
  actual: number
}

/**
 * 札行ごとに JEPX 制度上限 (価格・数量の値域) の違反を検出する (AC-BSP-050)。
 * 上下限に加えて刻み (価格 0.01 円/kWh・数量 0.1 MW) も検証する (= 提出確認モーダルの
 * violationBody が「0.1 刻み」を明示するため、 CSV インポート等で範囲内だが刻み違反の
 * 値が残っていても提出を止める必要がある)。上下限違反は step 判定より優先して報告する。
 *
 * 数量は制度上限・刻みとも MW 建てなので、札の `volumeMwh` (= 1 コマ 30 分の MWh) を
 * MW へ換算してから判定する (= `detectLimitViolations` の QTY 軸と同じ規則)。
 * 境界値ちょうど (0.01 / 999 円/kWh、0.1 / 9999.9 MW) は違反ではない。
 *
 * @param bids - 検証対象の札一覧
 */
/**
 * JEPX 制度上限の判定に必要な最小の形 (`InversionInput` と同じ理由で広げてある)。
 */
export type JepxLimitInput = Pick<
  SpotBidRow,
  | 'id'
  | 'areaCode'
  | 'timeCode'
  | 'direction'
  | 'orderMethod'
  | 'priceYenPerKwh'
  | 'volumeMwh'
>

/**
 * 価格 1 つだけで判定できる JEPX 制度上限違反 (上下限 → 刻みの順に 1 件返す)。
 *
 * 制度上限は**エリア・コマ・売買方向に依存しない**ので、 それらが未確定でも判定できる。
 * 札一覧 (`detectJepxLimitViolations`) と入力途中のモーダル (`bidFormAlerts`) の
 * 両方がここを通るので、 判定規則は 1 箇所にしかない。
 *
 * @param priceYenPerKwh - 円/kWh。 非有限値 (未入力) なら undefined を返す
 */
export const detectJepxPriceViolationKind = (
  priceYenPerKwh: number,
): JepxLimitViolationKind | undefined => {
  if (!Number.isFinite(priceYenPerKwh)) return undefined
  if (priceYenPerKwh < JEPX_PRICE_MIN_YEN_PER_KWH) return 'price-min'
  if (priceYenPerKwh > JEPX_PRICE_MAX_YEN_PER_KWH) return 'price-max'
  if (violatesJepxStep(priceYenPerKwh, JEPX_PRICE_CONSTRAINT.step)) {
    return 'price-step'
  }
  return undefined
}

/**
 * 数量 1 つだけで判定できる JEPX 制度上限違反 (上下限 → 刻みの順に 1 件返す)。
 *
 * @param volumeMw - MW (= 1 コマ 30 分の平均電力)。 非有限値なら undefined を返す
 */
export const detectJepxVolumeViolationKind = (
  volumeMw: number,
): JepxLimitViolationKind | undefined => {
  if (!Number.isFinite(volumeMw)) return undefined
  if (volumeMw < JEPX_VOLUME_MIN_MW) return 'volume-min'
  if (volumeMw > JEPX_VOLUME_MAX_MW) return 'volume-max'
  if (violatesJepxStep(volumeMw, JEPX_VOLUME_STEP_MW)) return 'volume-step'
  return undefined
}

/**
 * 札一覧から JEPX 制度上限違反を検出する。
 *
 * 成行 (AC-SPOT-006) の札は価格軸を検査しない。 spec `spot-bidding.md`
 * AC-BSP-004b が「成行注文… は価格を指定しないため本 AC の対象外」と定めており、
 * backend も指値だけに価格レンジを当てる。 ここで成行の `priceYenPerKwh: "0"` を
 * 指値として判定すると `price-min` (0 < 0.01) が立ち、 サーバが受理する札を
 * クライアントだけが承認申請でブロックする。
 */
export const detectJepxLimitViolations = (
  bids: readonly JepxLimitInput[],
): JepxLimitViolation[] => {
  const out: JepxLimitViolation[] = []
  for (const b of bids) {
    const at = {
      bidId: b.id,
      areaCode: b.areaCode,
      side: b.direction,
      timeCode: b.timeCode,
    }
    const price = Number(b.priceYenPerKwh)
    const priceKind = isMarketOrderRow(b)
      ? undefined
      : detectJepxPriceViolationKind(price)
    if (priceKind !== undefined) {
      out.push({ ...at, kind: priceKind, actual: price })
    }
    const volumeMw = Number(b.volumeMwh)
    const volumeKind = detectJepxVolumeViolationKind(volumeMw)
    if (volumeKind !== undefined) {
      out.push({ ...at, kind: volumeKind, actual: volumeMw })
    }
  }
  return out
}

/** MW 値を末尾ゼロなしで出す (0.08 → "0.08"、9999.9 → "9999.9")。 */
const mwText = (v: number): string => String(Number(v.toFixed(4)))

/**
 * JEPX 制度上限違反 1 件を承認申請確認モーダル用の 1 行明細に整形する
 * (AC-BSP-052: テナント設定上限違反とは別文言で「制度上限」であることを示す)。
 *
 *   価格: `コマ 25 ・ 東京 ・ 買: 価格 ¥1200.00（JEPX 制度上限 ¥0.01〜¥999.00）`
 *   数量: `コマ 3 ・ 東京 ・ 売: 数量 0.08 MW（JEPX 制度上限 0.1〜9999.9 MW）`
 */
export const formatJepxLimitViolationDetail = (
  v: JepxLimitViolation,
): string => {
  const t = i18next.t.bind(i18next)
  const head = detailHead(v.timeCode, v.areaCode, v.side)
  if (v.kind === 'price-step') {
    // 刻み違反は違反桁を丸めると見えなくなるため、 toFixed せず素の値のまま出す。
    return t('page:biddingSpot.violation.jepxPriceStepDetail', {
      head,
      actual: String(v.actual),
      step: JEPX_PRICE_CONSTRAINT.step,
    })
  }
  if (v.kind === 'volume-step') {
    return t('page:biddingSpot.violation.jepxVolumeStepDetail', {
      head,
      actual: mwText(v.actual),
      step: JEPX_VOLUME_STEP_MW,
    })
  }
  if (v.kind === 'volume-min' || v.kind === 'volume-max') {
    return t('page:biddingSpot.violation.jepxVolumeDetail', {
      head,
      actual: mwText(v.actual),
      min: mwText(JEPX_VOLUME_MIN_MW),
      max: mwText(JEPX_VOLUME_MAX_MW),
    })
  }
  return t('page:biddingSpot.violation.jepxPriceDetail', {
    head,
    actual: v.actual.toFixed(2),
    min: JEPX_PRICE_MIN_YEN_PER_KWH.toFixed(2),
    max: JEPX_PRICE_MAX_YEN_PER_KWH.toFixed(2),
  })
}

// ----- JEPX 階段数制限 (AC-BSP-021) -----

/**
 * 同一コマ × エリア × 売買方向あたりの階段数 (= 段階入札の札数) 上限。
 *
 * spec spot-bidding.md AC-BSP-021 の P0 暫定値 (「EN 実装時に JEPX 公式仕様を確認、
 * 暫定 50 階段 / コマ × エリア × 方向」)。proto は 1 入札 = 1 階段のモデルなので
 * 札数をそのまま階段数として数える (multi-step 入札は未モデル、design 同注記)。
 */
export const JEPX_STAIRS_LIMIT = 50

export type StairsExcess = {
  areaCode: AreaCode
  side: Side
  timeCode: number
  /** 当該グループの階段数 (= 札数)。 */
  count: number
}

/**
 * 階段数を数えるのに必要な最小 shape (= グループキーだけ)。
 *
 * `SpotBidRow` (承認申請ゲート) と `BulkGeneratedBid` / `ExistingBidVolume`
 * (一括作成 Step2 の事前判定、`models/bulkCreate.ts`) の双方を同じ関数で数えるために
 * 引数を最小化する。判定ロジック・上限値を 2 箇所に複製すると、申請時に止まるものが
 * プレビューでは OK に見える (= 本 PR で指摘された非対称) を再発させるため。
 */
export type StairsCountableBid = {
  areaCode: AreaCode
  timeCode: number
  direction: Side
}

/**
 * 同一コマ × エリア × 売買方向で階段数上限を超えるグループを検出する (AC-BSP-021)。
 * 超過数の多い順に返す (design `_stairViolations` の sort 準拠)。
 *
 * @param bids - 申請対象の札 (= design の submittableRows と同スコープ)
 */
export const detectStairsExcesses = (
  bids: readonly StairsCountableBid[],
  limit: number = JEPX_STAIRS_LIMIT,
): StairsExcess[] => {
  const counts = new Map<string, StairsExcess>()
  for (const b of bids) {
    const key = `${b.timeCode}|${b.areaCode}|${b.direction}`
    const entry = counts.get(key)
    if (entry === undefined) {
      counts.set(key, {
        areaCode: b.areaCode,
        side: b.direction,
        timeCode: b.timeCode,
        count: 1,
      })
    } else {
      entry.count += 1
    }
  }
  return [...counts.values()]
    .filter((e) => e.count > limit)
    .sort((a, b) => b.count - a.count)
}

/**
 * 階段数超過 1 件を明細行に整形する (design `bsc-stairs-list` 準拠)。
 *
 * 例: `コマ 25 ・ 東京 ・ 売: 51 階段（上限 50）`
 */
export const formatStairsExcessDetail = (
  e: StairsExcess,
  limit: number = JEPX_STAIRS_LIMIT,
): string =>
  i18next.t('page:biddingSpot.violation.stairsDetail', {
    head: detailHead(e.timeCode, e.areaCode, e.side),
    stairs: e.count,
    limit,
  })

// ----- 売買逆転検出 -----

export type Inversion = {
  bidIds: string[] // 逆転に巻き込まれている札 id 群 (= 同 area × 同 koma の buy + sell)
  areaCode: AreaCode
  timeCode: number
  /** 成行の札が絡む交差 (AC-BSP-009: 成行は対向札があれば価格を見ずに交差)。 */
  hasMarketOrder: boolean
  /** 指値の買いの最高値。 指値の買いが無ければ undefined。 */
  maxBuyPrice: number | undefined
  /** 指値の売りの最安値。 指値の売りが無ければ undefined。 */
  minSellPrice: number | undefined
}

/**
 * 同 area × 同 koma で「買い価格 ≥ 売り価格」 を検出する。
 * TODO(business-logic): 本来は backend が approval request の整合性 check として返す。
 */
/**
 * 逆転判定に必要な最小の形。 `SpotBidRow` はこれを満たすので従来の呼び出しはそのまま
 * 通る。 モーダルの入力途中の値 (= まだ札になっていない下書き) も判定に載せられるよう
 * 広げてある (`bidFormAlerts`)。
 */
export type InversionInput = Pick<
  SpotBidRow,
  | 'id'
  | 'areaCode'
  | 'timeCode'
  | 'direction'
  | 'priceYenPerKwh'
  | 'orderMethod'
>

/**
 * 逆転の判定規則そのもの: **買値 ≧ 売値** (境界の一致も逆転に含める)。
 *
 * グループ単位の `detectInversions` と、 入力途中の 1 札が当事者かを見る
 * `bidFormAlerts` の両方がここを通る。 規則を 2 箇所に書くと、 片方だけ
 * 変わった時に一覧の警告アイコンとモーダルの警告が食い違う。
 */
export const isInvertedPair = (buyPrice: number, sellPrice: number): boolean =>
  buyPrice >= sellPrice

/** 売買逆転の判定に出す片側の値: 指値の価格 (円/kWh)、 または成行。 */
export type SpotCrossPrice = number | 'market'

/**
 * 買い側と売り側の組が交差 (売買逆転) か (AC-BSP-009)。 どちらかが成行なら価格を見ずに
 * 交差とし (成行は価格 "0" を運ぶが指値 0 円ではない)、 指値どうしは [[isInvertedPair]]。
 * 一覧 (`detectInversions`) と個別作成・編集モーダル (`bidFormAlerts`) が通る、
 * 成行の規則の置き場所。 backend の `crossesPrice` と同じ形。
 */
export const crossesSpotPrice = (
  buy: SpotCrossPrice,
  sell: SpotCrossPrice,
): boolean => buy === 'market' || sell === 'market' || isInvertedPair(buy, sell)

const SPOT_INVERSION_POOL_CATEGORIES: ReadonlySet<SpotBidStatusCategory> =
  new Set<SpotBidStatusCategory>([
    'draft',
    'reserved',
    'pendingApproval',
    'rejected',
    'sending',
    'active',
  ])

/**
 * 売買逆転の検知対象プール (AC-BSP-009) に入る札か。 対象は AC-BSP-047 の 12 区分のうち
 * 下書き・予約中・承認待ち・差戻し・送信中・入札中。 約定待ち・取消・失効・全約定・部分約定・
 * 未約定は含めない。 区分は一覧のステータス列と同じ [[deriveSpotBidStatusCategory]] で
 * 決めるので、 `isPastDeadline` は一覧のステータス列に渡す値と揃える。
 * 一覧のバナー・ハイライトと個別作成・編集モーダルの警告は、 どちらも札をこの述語で絞ってから
 * 判定する。
 */
export const isInSpotInversionPool = (
  row: SpotBidStatusCategoryInput,
  isPastDeadline: boolean,
): boolean =>
  SPOT_INVERSION_POOL_CATEGORIES.has(
    deriveSpotBidStatusCategory(row, isPastDeadline),
  )

export const detectInversions = (
  bids: readonly InversionInput[],
): Inversion[] => {
  const groups = new Map<string, InversionInput[]>()
  for (const b of bids) {
    const key = `${b.areaCode}-${b.timeCode}`
    const arr = groups.get(key) ?? []
    arr.push(b)
    groups.set(key, arr)
  }
  const out: Inversion[] = []
  for (const arr of groups.values()) {
    const buys = arr.filter((b) => b.direction === Side.BUY)
    const sells = arr.filter((b) => b.direction === Side.SELL)
    if (buys.length === 0 || sells.length === 0) continue
    // 成行札 (price=0) は価格を持たないので、 価格の比較からは外す (外さないと成行売の 0 円が
    // 売りの最安値になる)。 買い側・売り側それぞれ、 成行があれば成行、 なければ指値の
    // 最高買値 / 最安売値で交差を判定する (AC-BSP-009。 入札価格を記録した成行の札
    // 〈AC-BDL-025、 [P1-1]〉は未対応)。
    const limitBuyPrices = buys
      .filter((b) => !isMarketOrderRow(b))
      .map((b) => Number(b.priceYenPerKwh))
    const limitSellPrices = sells
      .filter((b) => !isMarketOrderRow(b))
      .map((b) => Number(b.priceYenPerKwh))
    const hasMarketOrder = arr.some(isMarketOrderRow)
    const maxBuyPrice =
      limitBuyPrices.length > 0 ? Math.max(...limitBuyPrices) : undefined
    const minSellPrice =
      limitSellPrices.length > 0 ? Math.min(...limitSellPrices) : undefined
    const buyCross: SpotCrossPrice | undefined = buys.some(isMarketOrderRow)
      ? 'market'
      : maxBuyPrice
    const sellCross: SpotCrossPrice | undefined = sells.some(isMarketOrderRow)
      ? 'market'
      : minSellPrice
    const isInverted =
      buyCross !== undefined &&
      sellCross !== undefined &&
      crossesSpotPrice(buyCross, sellCross)
    if (isInverted) {
      out.push({
        bidIds: arr.map((b) => b.id),
        areaCode: arr[0].areaCode,
        timeCode: arr[0].timeCode,
        hasMarketOrder,
        maxBuyPrice,
        minSellPrice,
      })
    }
  }
  return out
}

// ----- ポジション chart 用 集計 (= 48 コマ × 3 系列) -----
//
// design HTML `design/bidding/spot.html` の spotPosSeries 定義準拠 (mock 07329b3c):
//   - executed (約定後カバー量、 緑 bar = 約定 MW 累計)
//   - need (調達必要量、 橙 stepAfter line)
//   - cover (入札後カバー量、 青 dashed stepAfter line = 入札 MW 累計)
// 旧 remain 系列 / 右軸 coveragePct 系列は design 刷新で廃止。 cover / executed の
// need クランプと描画は PositionChart 側で行う。
//
// 共通 PositionChartSlot 型 (= IntradayBidding と同じ shape) を再利用する。

// コマ別数量は need (backend GetProcurementNeed 由来) も spot 札 (`volumeMwh` 列に
// 入る値) も MW なので、 同一 `PositionChartSlot` (= MW 契約、 IntradayBidding の
// `volumeMw` と共有) にそのまま載せる (issue #4285。 単位換算を挟むと need に対して
// 倍/半分の高さで描画され、 充足率と札 prefill 数量がズレる)。
// コマ跨ぎの合計 (`sumKomaMwToMwh`) だけは MWh — SoT は `~/models/komaQuantity`
// (スポットの合計欄は AC-BSP-053 上は MW。 差し戻しは issue #6266)。

/**
 * 札一覧から 48 コマの集計を作る (= PositionChart 用)。
 *
 * - cover (入札後カバー量、 「全札（下書き含む）」線) = コマあたりの **net 充当**
 *   (= 買 − 売、 MW)。 取消・失効の札は除く (AC-BSP-108)。
 *   need>0 (不足) は買いで、 need<0 (余剰) は売りで埋めるため、 net にすると need と同符号で
 *   伸びて need 線に近づく (#2533、 業務正本は mock-app `JepxSpotBid.razor.cs` `BuildGapData`
 *   の `min(|need|, buy−sell)`)。 gross-sum (買+売) では余剰コマの売り札が不足方向に加算され、
 *   売れば売るほど need 線から離れる表示になるため採らない。
 * - coverSubmitted (入札後カバー量、 「入札済み」線) = cover の部分集合。 承認済（内部の
 *   過渡状態）・送信中・予約中・入札中・約定待ち・全約定・部分約定・未約定の札のみを net 充当する
 *   (AC-BSP-108。 区分の判定は `deriveSpotBidStatusCategory` に委ね、 12 区分 → 2 本の写像は
 *   `coverLineOfSpotCategory` が SoT)。
 * - executed (約定後カバー量) = コマあたりの約定量の net 充当 (= execVolumeMwh の 買 − 売、
 *   GC 後のみ)。 同上。
 * - need (調達必要量) = backend GetProcurementNeed 由来の RPC 実データ (needByKoma[i]、 MW)。
 *   取得エラー時は needByKoma=undefined を渡し、 mock を捏造せず need=0 とする
 *   (設計書 bidding-position-need.md §7 採番、恒久 spec 未反映)。
 *
 * |need| クランプは AC-BSP-016b (クランプ廃止、 #4570) で撤去した。 本関数は net をそのまま
 * 返し、 描画側 (`buildPositionChartData`) もクランプしない。
 *
 * 取下が成立した (約定軸 CANCELLED、 AC-BSP-081) 札は板から下りて枠が解放されるため
 * カバー量の集計から外す (spec Discussion「取消・失効の札は…ポジションチャートから
 * 除外し、 取消枠は解放する」)。 取下処理中 (WITHDRAWING) はまだ成立していないため外さない。
 *
 * 発注主体区分が空欄の札は cover にも executed にも入れず `unknownIssuerMw` に数える
 * (AC-BSP-127、 #10084。 時間前の `buildIntradayPositionSlots` と同じ扱い)。
 *
 * @param isPastDeadline - 入札締切 (`spotDeadlineAt`) を過ぎているか。 承認申請中 / 送信失敗の
 *   札が締切を過ぎたまま残っている時に「失効」へ倒す代理判定に使う
 *   (`deriveSpotBidStatusCategory` 参照)。
 */
export const buildPositionSlots = (
  bids: SpotBidRow[],
  needByKoma: readonly number[] | undefined,
  isPastDeadline: boolean,
): PositionChartSlot[] => {
  const lines = createKomaCoverLines()
  const unknownIssuerByKoma = new Map<number, number>()
  // 約定後カバー量 (= FillMw 累計)。 約定済 (execVolumeMwh あり) の札のみ加算する。
  const execBuyByKoma = new Map<number, number>()
  const execSellByKoma = new Map<number, number>()
  for (const b of bids) {
    // 区分と量は札ごとに 1 回だけ決め、 カバー線と区分不明の札の両方へ同じ値を渡す。
    const category = deriveSpotBidStatusCategory(b, isPastDeadline)
    const coverLine = coverLineOfSpotCategory(category)
    // 入札時カバー量は約定結果で書き換えないのが原則 (AC-BSP-016b) だが、 部分約定だけは
    // 札が終わり残りはもう埋まらないための例外で約定した量 (AC-BSP-108)。
    const vol = coverBidMw({
      partialMatched: category === 'partialMatched',
      volumeMw: Number(b.volumeMwh),
      execVolumeMw:
        b.execVolumeMwh === undefined ? undefined : Number(b.execVolumeMwh),
    })
    const isSell = b.direction === Side.SELL
    if (b.direction !== Side.BUY && !isSell) continue
    const isUnknownIssuer = b.issuerType === 'unknown'
    addToKomaCoverLines(lines, coverLine, {
      komaKey: b.timeCode,
      isSell,
      volume: vol,
      countsToCover: !isUnknownIssuer,
    })
    if (coverLine === 'excluded') continue
    if (isUnknownIssuer) {
      const unknownMw = unknownIssuerBidMw({
        counted: UNKNOWN_ISSUER_COUNTED_CATEGORIES.has(category),
        direction: b.direction,
        mw: vol,
      })
      if (unknownMw !== undefined) {
        unknownIssuerByKoma.set(
          b.timeCode,
          (unknownIssuerByKoma.get(b.timeCode) ?? 0) + unknownMw,
        )
      }
      continue
    }
    if (b.execVolumeMwh !== undefined) {
      const execTarget = isSell ? execSellByKoma : execBuyByKoma
      execTarget.set(
        b.timeCode,
        (execTarget.get(b.timeCode) ?? 0) + Number(b.execVolumeMwh),
      )
    }
  }
  return Array.from({ length: 48 }, (_, i) => {
    const index = i + 1
    // cover / executed / need はいずれもコマ別 MW (issue #4285)。
    const all = readKomaLine(lines.all, index)
    const submitted = readKomaLine(lines.submitted, index)
    const executed =
      (execBuyByKoma.get(index) ?? 0) - (execSellByKoma.get(index) ?? 0)
    const need = needByKoma?.[i] ?? 0
    return {
      index,
      coverMw: all.coverMw,
      coverSubmittedMw: submitted.coverMw,
      needMw: need,
      // 過剰入札時は負値 (超過量) になる (AC-BSP-016b のクランプ廃止に合わせ 0 で止めない)。
      // prefill は呼び出し側が remainMw>0 のときだけ使う (「全札」基準)。
      remainMw: resolveRemainMw(all.coverMw, need),
      executedMw: executed,
      unknownIssuerMw: unknownIssuerByKoma.get(index),
      buyMw: all.buyMw,
      sellMw: all.sellMw,
      buySubmittedMw: submitted.buyMw,
      sellSubmittedMw: submitted.sellMw,
      // 入札時カバー率は cover (「全札」基準) ・符号保持・クランプなし (AC-BSP-016b)。
      coveragePct: need !== 0 ? (all.coverMw / need) * 100 : undefined,
    }
  })
}

/**
 * 通常入札とブロック入札の 48 コマ slot を合算して 1 本の共有チャート slot を返す
 * (spec spot-bidding.md AC-016: 同一チャート共有)。
 * cover / executed は通常＋ブロックの和、 need は両者で同一 (どちらも同じ RPC need を
 * 注入するため normal 側を採用)。remainMw は合算後 cover で need − cover を再計算する。
 * design 上スポットは通常/ブロック両タブで同一チャートを共有するため、 page が本関数で
 * 組んだ slot を両タブへ供給する。
 *
 * `executedNormalMw` / `executedBlockMw` は合算前の各市場の `executedMw` をそのまま
 * 運ぶ (= `buildPositionSlots` / `buildBlockPositionSlots` の出力は元々どちらか一方の
 * 市場の値しか持たない)。 チャートが約定 (通常)・約定 (ブロック) を積み上げ棒で別々に
 * 描く (AC-BSP-016) ための内訳で、 本関数だけが埋める (通常/ブロックの区別が無い時間前
 * 市場の `PositionChartSlot` はこの 2 フィールドを持たない)。
 */
export const combineSpotPositionSlots = (
  normal: PositionChartSlot[],
  block: PositionChartSlot[],
): PositionChartSlot[] =>
  Array.from({ length: 48 }, (_, i) => {
    const n = normal[i]
    const b = block[i]
    const need = n?.needMw ?? b?.needMw ?? 0
    const executedNormal = n?.executedMw ?? 0
    const executedBlock = b?.executedMw ?? 0
    // cover / executed は net (買 − 売) なので通常＋ブロックの単純和で net が保たれる
    // (= |need| クランプは描画側なので、 クランプ済み値を足す二重クランプにならない)。
    const cover = (n?.coverMw ?? 0) + (b?.coverMw ?? 0)
    const coverSubmitted =
      (n?.coverSubmittedMw ?? 0) + (b?.coverSubmittedMw ?? 0)
    const executed = executedNormal + executedBlock
    const unknownIssuerMw =
      n?.unknownIssuerMw === undefined && b?.unknownIssuerMw === undefined
        ? undefined
        : (n?.unknownIssuerMw ?? 0) + (b?.unknownIssuerMw ?? 0)
    return {
      index: i + 1,
      coverMw: cover,
      coverSubmittedMw: coverSubmitted,
      needMw: need,
      remainMw: resolveRemainMw(cover, need),
      executedMw: executed,
      executedNormalMw: executedNormal,
      executedBlockMw: executedBlock,
      unknownIssuerMw,
      buyMw: (n?.buyMw ?? 0) + (b?.buyMw ?? 0),
      sellMw: (n?.sellMw ?? 0) + (b?.sellMw ?? 0),
      buySubmittedMw: (n?.buySubmittedMw ?? 0) + (b?.buySubmittedMw ?? 0),
      sellSubmittedMw: (n?.sellSubmittedMw ?? 0) + (b?.sellSubmittedMw ?? 0),
      coveragePct: need !== 0 ? (cover / need) * 100 : undefined,
    }
  })

// エリア × 売買 の約定集計は spec AC-BSP-002b の 8 列 (= 縦持ち) へ作り直し、 時間前入札と
// 共用する共通コンポーネント側へ移した:
//   集計   = ~/components/bidding/common/executionSummary (computeExecutionAreaSummary)
//   札の写像 = ~/pages/SpotBidding/models/spotExecutionSummary

// ----- 集計済み充足率 (= total cover / total need × 100) -----

export type CoverageSummary = {
  /**
   * 全コマ合算 cover の電力量 MWh (= 各コマ net 充当 買−売 (MW) の和 × 0.5h。
   * need と同符号で積まれるので、 全コマを狙いどおり埋めると
   * totalCoverMwh ≒ totalNeedMwh になる)。
   *
   * コマ別 MW をコマ跨ぎで足した値は電力でも電力量でもないため、 表示に出す合計は
   * `sumKomaMwToMwh` で電力量 MWh にしている (MW 単純合算への差し戻しは issue #6266)。
   */
  totalCoverMwh: number
  /** 全コマ合算 need の電力量 MWh。 */
  totalNeedMwh: number
  /** 充足率 % (= cover / need × 100、 need=0 のとき undefined)。 */
  coverageRate?: number
  /** 状態の語 (= 「買い過剰」「逆方向」等)。 need=0 の placeholder では undefined。 */
  coverageStateKey?: CoverageState['key']
  /** design `js/coverage-state.js` の cls と 1:1 (= {@link resolveCoverageState})。 */
  state: CoverageTone
}

/**
 * 全コマの cover / need を合算して 充足率 summary を返す。
 *
 * 率は上下ともクランプしない (= 過剰 208% / 逆方向 −80% を実値で出す。 AC-BSP-016b)。
 * 状態の判定は tooltip と共有の {@link resolveCoverageState} に委ねる (= 写経しない)。
 * totalNeed=0 は算出不能なので neutral (調達必要量 未設定 placeholder)。
 */
export const summarizeCoverage = (
  positions: PositionChartSlot[],
): CoverageSummary => {
  let totalCoverMw = 0
  let totalNeedMw = 0
  for (const p of positions) {
    totalCoverMw += p.coverMw
    totalNeedMw += p.needMw
  }
  // 表示用の合計は電力量 (MWh) にする。 率は同じ次元同士の除算なので MW / MWh の
  // どちらで割っても同値 (= 0.5h が約分される)。
  const totalCoverMwh = sumKomaMwToMwh(totalCoverMw)
  const totalNeedMwh = sumKomaMwToMwh(totalNeedMw)
  if (totalNeedMwh === 0) {
    return { totalCoverMwh, totalNeedMwh, state: 'neutral' }
  }
  // cover は net 充当 (買−売) で need と同符号に積まれるため、 余剰 (need<0) を
  // 売りで充当すると cover も負になる。 率は符号付き同士で除算する (= 負÷負 で
  // 正の充足率。 |need| 分母のままだと正しく売り充当したのに負率になる)。
  const rate = (totalCoverMwh / totalNeedMwh) * 100
  const coverage = resolveCoverageState(rate, totalNeedMwh)
  return {
    totalCoverMwh,
    totalNeedMwh,
    coverageRate: rate,
    coverageStateKey: coverage.key,
    state: coverage.tone,
  }
}
